"use client";

import StatusContainer from "@/components/containers/status-container";
import { ConfirmationPageTrackingEvent } from "@/components/events/confirmation-page-tracking-event";
import TrackingEvent, {
  useTrackEvent,
} from "@/components/events/tracking-event";
import { GiftingPaymentSuccess } from "@/components/payment-complete/gifting-payment-success";
import { ContinueBookingButton } from "@/components/nav-button/continue-booking-button";
import { assert } from "@/lib/utils";
import {
  Attribution,
  GetSessionV2Response,
} from "@endavaaccorplus/payments-common/schemas";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import type { getCompletionContent } from "./actions";
import { SearchParams } from "./page";
import { CompleteOrderDetailsV2 } from "@/components/complete-order-details-v2";
import { StatusReferenceIds } from "@/components/status-reference-ids";
import { getGtmPurchaseEventProps } from "../../../lib/checkout/gtm";
import type { IntegrationSource } from "@/lib/checkout/integration-sources";
import { CompletionStatusButtons } from "./completion-status-buttons";
import { isGiftRedemptionSession } from "@/lib/checkout/is-gift-redemption-session";
import { getGiftRedemptionSuccessCtaEvent } from "@/lib/tracking/gift-redemption-success-cta";

type PaymentCompleteProps = {
  transactionFailed: React.ReactNode;
  transactionPending: React.ReactNode;
  membershipFulfilmentFailed: React.ReactNode;
  completionContent: Awaited<ReturnType<typeof getCompletionContent>> | null;
  session: GetSessionV2Response;
  attributionData?: Attribution;
  integrationSource: IntegrationSource;
};

const PaymentComplete = ({
  transactionFailed,
  transactionPending,
  membershipFulfilmentFailed,
  completionContent,
  session,
  attributionData,
  integrationSource,
}: PaymentCompleteProps) => {
  const params = useSearchParams();
  const trackEvent = useTrackEvent();
  const { order_id, status_id, sessionId, status } = Object.fromEntries(
    params.entries(),
  ) as SearchParams;
  const isGiftRedemption = isGiftRedemptionSession(session);

  const trackGiftRedemptionSuccessCta = (
    role: "primary" | "secondary",
    href?: string | null,
    label?: string | null,
  ) => {
    if (!isGiftRedemption) return;

    trackEvent(getGiftRedemptionSuccessCtaEvent({ href, label, role }), {});
  };

  assert(session.product, "Must have session.product");

  const purchaseEventProps = useMemo(
    () =>
      getGtmPurchaseEventProps({
        session,
        transactionId: order_id,
        transactionStatusId: status_id,
        attributionData,
        integrationSource,
      }),
    [session, order_id, status_id, attributionData, integrationSource],
  );

  switch (status) {
    case "PAYMENT_POLL_TIMEOUT":
      return (
        <>
          <ConfirmationPageTrackingEvent
            state="deferred"
            integrationSource={integrationSource}
          />
          {transactionPending}
        </>
      );
    case "MEMBERSHIP_FULFILMENT_FAILURE":
    case "MEMBERSHIP_FULFILMENT_TIMEOUT":
      return (
        <>
          <ConfirmationPageTrackingEvent
            state="failed"
            integrationSource={integrationSource}
          />
          {membershipFulfilmentFailed}
        </>
      );
    case "JUSPAY_DECLINED":
    case "AUTHORIZATION_FAILED":
    case "AUTHENTICATION_FAILED":
    case "PWP_TRANSACTION_NOT_CREATED":
    case "PARTNER_TRANSACTION_NOT_CREATED": {
      return (
        <>
          <ConfirmationPageTrackingEvent
            state="failed"
            integrationSource={integrationSource}
          />
          {transactionFailed}
        </>
      );
    }
    case "CHARGED":
    case "COD_INITIATED":
    case "AUTHORIZED":
    case "PWP_NEW":
    case "PARTNER_NEW": {
      if (session.type === "gift-public") {
        const giftingPaymentSuffix = "/gifting-registration/payment";
        const checkoutPathname = session.checkoutUrl.pathname;

        assert(
          checkoutPathname.endsWith(giftingPaymentSuffix),
          "Gift session checkout URL must use the gifting payment route",
        );

        const giftingLandingPathname = checkoutPathname.slice(
          0,
          -giftingPaymentSuffix.length,
        );

        return (
          <GiftingPaymentSuccess
            giftingLandingPathname={giftingLandingPathname}
            purchaseEventProps={purchaseEventProps}
          />
        );
      }

      assert(completionContent, "Missing payment completion content");

      return (
        <>
          <ConfirmationPageTrackingEvent
            state="success"
            integrationSource={integrationSource}
          />
          <StatusContainer
            heading={completionContent.heading}
            title={completionContent.eyebrow}
            desc={completionContent.message}
            iconSrc={completionContent.icon ?? undefined}
            button={
              <CompletionStatusButtons
                content={completionContent}
                secondaryOnLinkClick={() =>
                  trackGiftRedemptionSuccessCta(
                    "secondary",
                    completionContent.secondaryCtaUrl,
                    completionContent.secondaryCtaText,
                  )
                }
                primaryCta={
                  completionContent.ctaUrl ? (
                    <ContinueBookingButton
                      href={completionContent.ctaUrl}
                      trackContinueBooking={
                        integrationSource === "ALL Hotel Sales Funnel"
                      }
                      integrationSource={integrationSource}
                      onClick={() =>
                        trackGiftRedemptionSuccessCta(
                          "primary",
                          completionContent.ctaUrl,
                          completionContent.ctaText,
                        )
                      }
                    >
                      {completionContent.ctaText}
                    </ContinueBookingButton>
                  ) : undefined
                }
              />
            }
          >
            <StatusReferenceIds orderId={order_id} />
            {session.product.salesRegion === "IN" ? (
              <CompleteOrderDetailsV2
                price={
                  "paymentAmount" in session
                    ? session.paymentAmount.price
                    : session.purchaseAmount.price
                }
                currencyCode={session.purchaseAmount.currencyCode}
                orderId={order_id}
                showOrderId={false}
              />
            ) : undefined}
          </StatusContainer>
          {session.type === "renew-public" && (
            <TrackingEvent
              eventName="renewFlowCompleted"
              eventProps={{
                pmId: session.membership.pmId,
                sessionId,
              }}
            />
          )}
          {isGiftRedemption && (
            <TrackingEvent
              eventName="gift_membership_redeemed"
              eventProps={{ order_id }}
            />
          )}
          <TrackingEvent eventName="purchase" eventProps={purchaseEventProps} />
        </>
      );
    }
    default:
      throw new Error("Unexpected Payment Transaction Status");
  }
};

export default PaymentComplete;
